import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

// FIELD BY FIELD, NEVER LOST (his plan: "a slow connection never loses a typed draft field"). What a person types is
// kept on this device until the server says saved; a field someone else changed since is shown, never overwritten.
const read = (k) => { try { return JSON.parse(localStorage.getItem(k) || "null"); } catch { return null; } };
const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* full storage: still saving */ } };
const drop = (k) => { try { localStorage.removeItem(k); } catch { /* nothing kept */ } };

export function useDraftField({ kind, id, row, onSaved }) {
  const lsKey = `fas-draft:${kind}:${id}:${row.key}`;
  let kept = read(lsKey);
  if (kept && kept.base !== row.rev && JSON.stringify(kept.value ?? null) === JSON.stringify(row.value ?? null)) { drop(lsKey); kept = null; } // it did land
  const [val, setVal] = useState(kept && kept.base === row.rev ? kept.value : row.value);
  const [status, setStatus] = useState(kept && kept.base === row.rev ? "kept" : "saved");
  const [conflict, setConflict] = useState(kept && kept.base !== row.rev ? { value: row.value, rev: row.rev, mine: kept.value } : null);
  const [error, setError] = useState(null);
  const live = useRef(status);
  live.current = status;
  useEffect(() => { if (live.current === "saved") setVal(row.value); }, [row.value, row.rev]);

  const save = async (value = val, base = row.rev) => {
    write(lsKey, { value, base });
    setStatus("saving"); setError(null);
    try {
      const { data } = await api.put(`/sales/draft/${kind}/${id}/field/${row.key}`, { value, base });
      drop(lsKey); setConflict(null); setStatus("saved"); onSaved(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "changed") { setConflict({ ...d, mine: value }); setStatus("conflict"); }
      else if (!e.response) setStatus("kept");
      else { drop(lsKey); setStatus("refused"); setError(formatApiErrorDetail(d)); toast.error(formatApiErrorDetail(d)); }
    }
  };
  const takeTheirs = () => { drop(lsKey); setVal(conflict.value); setConflict(null); setStatus("saved"); };
  const keepMine = () => save(conflict.mine, conflict.rev);
  const change = (v, now = false) => { setVal(v); setStatus("dirty"); write(lsKey, { value: v, base: row.rev }); if (now) save(v); };
  useEffect(() => { if (status === "kept") save(val); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {      // kept on this device: saves by itself the moment the connection is back
    const back = () => { if (live.current === "kept") save(); };
    window.addEventListener("online", back);
    return () => window.removeEventListener("online", back);
  });
  return { val, change, save, status, conflict, takeTheirs, keepMine, error };
}

export const STATUS_WORDS = {
  saved: "", dirty: "Not saved yet", saving: "Saving…", refused: "Not saved",
  kept: "Kept on this device — not saved yet. It saves when the connection is back.", conflict: "",
};
